<template>
  <div class="px-2 mb-2">
    <div class="d-flex align-center mb-2">
      <p class="font-weight-500 font-size-18 ma-0 me-2">
        {{
          $t('ai_features.organization_ai_settings.organization_ai_managers')
        }}
      </p>

      <AssigneesSearchPopover
        :allowSearchScopeChange="false"
        :filters="{
          isOrganizationAIManager: false,
        }"
        :includeCurrentAccountAtTop="false"
        :initialSearchScope="$constants.SEARCH_SCOPES.global"
        :targetOrganizationId="organization._id"
        @change="addOrganizationAIManager"
      >
        <CreateButton small :tooltipText="$t('people.add_user')" />
      </AssigneesSearchPopover>
    </div>

    <p
      class="font-size-14 caption blue-grey--text mb-6"
      style="max-width: 600px"
    >
      {{
        $t(
          'ai_features.organization_ai_settings.organization_ai_managers_description'
        )
      }}
    </p>

    <v-card outlined>
      <v-data-table
        calculate-widths
        data-private
        :footer-props="footerOptions"
        :headers="headers"
        :items="organizationAIManagers"
        :loading="isLoadingUsers"
        :options.sync="options"
        :server-items-length="totalRecordsInData"
      >
        <template v-slot:[`item.account`]="{ item }">
          <ChipAvatar
            link
            outlined
            small
            :account="item"
            @click="goToProfile(item)"
          />
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <Button
            color="blueGrey"
            icon="mdi-trash-can-outline"
            @click.stop="handleDelete(item)"
          />
        </template>
      </v-data-table>

      <DeleteConfirmationModal
        :isLoading="isDeleting"
        :show="showDeleteOrganizationAIManagerConfirmation"
        :title="
          $t('ai_features.organization_ai_settings.remove_ai_manager_title')
        "
        :text="
          $t('ai_features.organization_ai_settings.remove_ai_manager_message', {
            userName: getFullName(organizationAIManagerToDelete),
          })
        "
        @cancel="cancelOrganizationAIManagerDelete"
        @delete="confirmOrganizationAIManagerDelete"
      />
    </v-card>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import api from '@/api'

import AssigneesSearchPopover from '@/components/AssigneesSearchPopover/AssigneesSearchPopover.vue'
import CreateButton from '@/components/UI/CreateButton.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import Button from '@/components/UI/Button.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

const DEFAULT_SIZE = 50

export default {
  name: 'OrganizationAIManagers',

  mixins: [mixin],

  components: {
    AssigneesSearchPopover,
    CreateButton,
    ChipAvatar,
    Button,
    DeleteConfirmationModal,
  },

  props: {
    organization: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      totalRecordsInData: 0,
      headers: [
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'account',
          show: true,
        },
        {
          value: 'actions',
          text: this.$t('common.action_other'),
          sortable: false,
          show: true,
          align: 'start',
          width: 100,
        },
      ],
      isLoadingUsers: false,
      organizationAIManagers: [],
      showDeleteOrganizationAIManagerConfirmation: false,
      organizationAIManagerToDelete: null,
      isDeleting: false,
    }
  },

  computed: {
    options: {
      get() {
        return {
          itemsLength: this.totalRecordsInData,
          itemsPerPage: DEFAULT_SIZE,
          multiSort: false,
          mustSort: true,
          page: 1,
        }
      },
      set(event) {
        this.fetchOrganizationAIManagers({
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: event.sortDesc[0],
          sortBy: event.sortBy[0],
        })
      },
    },
  },

  methods: {
    ...mapActions({
      updateAccount: 'updateAccount',
    }),

    async fetchOrganizationAIManagers(options) {
      this.isLoadingUsers = true

      const itemsPerPage = options.itemsPerPage || DEFAULT_SIZE
      const sort = {
        by: options.sortBy ? options.sortBy : 'updatedAt',
        order: options.sortDesc ? 'desc' : 'asc',
      }

      try {
        const response = await api.account.getAccounts(this.organization?._id, {
          filters: { isOrganizationAIManager: true },
          from: (options.page - 1) * itemsPerPage,
          limit: itemsPerPage,
          sort,
        })

        this.organizationAIManagers = response?.data?.results ?? []
        this.totalRecordsInData = response?.data?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoadingUsers = false
      }
    },

    async addOrganizationAIManager(selectedUsers) {
      await this.updateAccountOrganizationAIManagerSetting(
        selectedUsers?.items?.[0],
        true
      )
      this.fetchOrganizationAIManagers(this.options)
    },

    async updateAccountOrganizationAIManagerSetting(selectedUser, value) {
      try {
        await this.updateAccount({
          account: selectedUser,
          data: { isOrganizationAIManager: value },
        })
        this.fetchOrganizationAIManagers(this.options)
        this.showSuccess(
          this.$t('ai_features.user_access_control.settings_save_success')
        )
      } catch (error) {
        this.showError(
          this.$t(
            'ai_features.user_access_control.organization_ai_manager_error'
          )
        )
      }
    },

    handleDelete(user) {
      this.organizationAIManagerToDelete = user
      this.showDeleteOrganizationAIManagerConfirmation = true
    },

    cancelOrganizationAIManagerDelete() {
      this.showDeleteOrganizationAIManagerConfirmation = false
      this.organizationAIManagerToDelete = null
    },

    async confirmOrganizationAIManagerDelete() {
      this.isDeleting = true
      await this.updateAccountOrganizationAIManagerSetting(
        this.organizationAIManagerToDelete,
        false
      )
      this.isDeleting = false
      this.showDeleteOrganizationAIManagerConfirmation = false
      this.organizationAIManagerToDelete = null
      this.fetchOrganizationAIManagers(this.options)
    },
  },
}
</script>
